/**
 * The recall flow's decisions, as values — `screens.md` §11, plan U48.
 *
 * Every string a non-negotiable makes verbatim lives here, so a test reads the same constant
 * the screen renders. Lot values are matched exactly and case-sensitively and are never
 * altered (§3.8, `recallScopeSchema`).
 */
import {
  RECALL_SCOPE_OF_SERVICE,
  recallNoticeSchema,
  recallScopeSchema,
  type BatchSummary,
  type ProblemBody,
  type RecallScope,
} from "@packagepage/shared";

export const SCOPE_OF_SERVICE = RECALL_SCOPE_OF_SERVICE;

export const EXACT_MATCH_WARNING =
  "Lot codes must match the printed code exactly — LOT42 and Lot 42 are different lots to a " +
  "scanner.";

export const CARVE_OUT =
  "Keeping batch records is part of Growth. Publishing a recall notice is not, and never " +
  "will be — it works identically on every plan.";

export const NEW_TO_US =
  "We don't have a record of this lot, which is fine — we'll create one now so the notice " +
  "can be scoped to it. You can add its expiry date later.";

export const NOT_SURE =
  "Recall the whole product — you can narrow the scope later. Over-covering is safe; " +
  "under-covering is dangerous.";

export const NOT_FOUND_MECHANISM =
  "Codes not generated here don't route through us, so a notice can't reach those scans. If " +
  "this product's barcode was made somewhere else, your recall has to go out through the " +
  "channels that do reach those units — retailers, distributors, and your regulator.";

export const OVERRIDE_LABEL = "Send scans of affected units to this notice — recommended";

export const OVERRIDE_ON =
  "A phone camera doesn't ask for a specific kind of link, so without this a shopper " +
  "scanning an affected unit would still land on the normal product page. Switching it off " +
  "leaves the notice reachable only by apps that request it by name.";

export const OVERRIDE_OFF =
  "Off: a shopper who scans an affected unit with a phone camera lands on the normal product " +
  "page, not this notice. Only apps that request a recall notice by name will find it.";

export const PUBLISH_LABEL = "Publish recall notice — live everywhere in ~5 seconds";

/** Field order and labels for the built-in notice; keys are `GPSR_NOTICE_FIELDS`'. */
export const NOTICE_FIELDS = [
  { key: "hazard", label: "The hazard · what's wrong" },
  { key: "consumerAction", label: "What the consumer should do" },
  { key: "contact", label: "Contact" },
] as const;

export type NoticeKey = (typeof NOTICE_FIELDS)[number]["key"];

/** The language the built-in notice is keyed under. The handoff draws no language choice. */
export const NOTICE_LANG = "en";

export interface RecallForm {
  readonly productId: string;
  readonly scope: RecallScope;
  /** What the person typed, unparsed. */
  readonly lotText: string;
  readonly tab: "builtIn" | "external";
  readonly fields: Readonly<Record<NoticeKey, string>>;
  readonly externalUrl: string;
  readonly overrideDefault: boolean;
}

export const EMPTY_RECALL_FORM: RecallForm = {
  productId: "",
  scope: "lots",
  lotText: "",
  tab: "builtIn",
  fields: { hazard: "", consumerAction: "", contact: "" },
  externalUrl: "",
  overrideDefault: true,
};

/**
 * Lot values in the order typed, repeats dropped.
 *
 * Whitespace separates because GS1 charset X has no whitespace, so no lot can contain it;
 * splitting on commas alone would send `" LOT77"` for `"LOT42, LOT77"`.
 */
export function splitLots(text: string): string[] {
  return [...new Set(text.split(/[\s,]+/).filter((lot) => lot !== ""))];
}

export function lotsNotOnFile(
  lots: readonly string[],
  batches: readonly Pick<BatchSummary, "lot">[],
): string[] {
  const onFile = new Set(batches.map((batch) => batch.lot));
  return lots.filter((lot) => !onFile.has(lot));
}

function listed(lots: readonly string[]): string {
  if (lots.length <= 1) return lots[0] ?? "";
  return `${lots.slice(0, -1).join(", ")} and ${lots[lots.length - 1] as string}`;
}

/** The confirmation's consequence sentence — `screens.md` §11's worked example is one lot. */
export function scopeSentence(scope: RecallScope, lots: readonly string[]): string {
  if (scope === "gtin")
    return "Scans of every unit of this product will see this notice, whatever lot they carry.";
  return `Scans of ${listed(lots)} will see this notice. Scans of any other lot will not.`;
}

/** The fixed affected-product block: name, GTIN, lots, and expiry where a batch has one. */
export function affectedProducts(
  name: string,
  gtin14: string,
  scope: RecallScope,
  lots: readonly string[],
  batches: readonly Pick<BatchSummary, "lot" | "expiry">[],
): string {
  if (scope === "gtin") return `${name} · GTIN ${gtin14} · every lot`;
  const expiry = new Map(batches.map((batch) => [batch.lot, batch.expiry]));
  const described = lots.map((lot) => {
    const date = expiry.get(lot);
    return date === null || date === undefined ? lot : `${lot} (expiry ${date})`;
  });
  return `${name} · GTIN ${gtin14} · ${described.length === 1 ? "lot" : "lots"} ${described.join(", ")}`;
}

/** The request body. Empty notice fields are omitted: the page skips them anyway. */
export function toPublishBody(
  form: RecallForm,
  affected: string,
): Record<string, unknown> {
  const lots = form.scope === "lots" ? splitLots(form.lotText) : [];
  const base = {
    productId: form.productId,
    scope: form.scope,
    ...(form.scope === "lots" ? { lots } : {}),
    overrideDefault: form.overrideDefault,
  };
  if (form.tab === "external")
    return { ...base, externalUrl: form.externalUrl, notice: {} };

  const fields: Record<string, string> = { affectedProducts: affected };
  for (const { key } of NOTICE_FIELDS) {
    if (form.fields[key].trim() !== "") fields[key] = form.fields[key];
  }
  return { ...base, notice: { [NOTICE_LANG]: fields } };
}

/**
 * The op's own stage checks, in the op's order, before the call. `null` means send it.
 *
 * With a live recall on the product the op refuses `RECALL_ALREADY_ACTIVE` before it reads
 * the scope (R6), so the form checks nothing and lets the server say that first.
 */
export function preflight(body: unknown, productHasActiveRecall: boolean): string | null {
  if (productHasActiveRecall) return null;
  for (const stage of [recallScopeSchema, recallNoticeSchema]) {
    const parsed = stage.safeParse(body);
    if (!parsed.success)
      return parsed.error.issues[0]?.message ?? "The request is not valid.";
  }
  return null;
}

/** A warning as the response schema parses it: `code` is a string, not the registry union. */
export interface Advisory {
  readonly code: string;
  readonly title: string;
  readonly detail: string;
}

export interface PublishOutcome {
  readonly recallId: string;
  /** `RECALL_NOT_RESOLVING`'s detail when the op returned it, else null. */
  readonly notResolving: Advisory | null;
  /** Every other advisory, rendered by its own detail. */
  readonly otherWarnings: readonly Advisory[];
}

export function publishOutcome(response: {
  recall: { id: string };
  warnings: readonly Advisory[];
}): PublishOutcome {
  return {
    recallId: response.recall.id,
    notResolving:
      response.warnings.find((w) => w.code === "RECALL_NOT_RESOLVING") ?? null,
    otherWarnings: response.warnings.filter((w) => w.code !== "RECALL_NOT_RESOLVING"),
  };
}

export interface RefusalView {
  readonly title: string;
  readonly detail: string;
  readonly alreadyActive: boolean;
}

/** A refusal renders the body's own `detail`; a failure with no body says what is unknown. */
export function refusalView(problem: ProblemBody | null): RefusalView {
  if (problem === null) {
    return {
      title: "We could not confirm whether the notice was published",
      detail:
        "The request did not come back with an answer. Check the dashboard for a live " +
        "recall on this product before publishing again.",
      alreadyActive: false,
    };
  }
  return {
    title: problem.title,
    detail: problem.detail,
    alreadyActive: problem.code === "RECALL_ALREADY_ACTIVE",
  };
}

/** Where a scan of an affected unit goes once the notice is live. */
export function noticeAddress(
  resolverBaseUrl: string,
  noticePath: string,
  externalUrl: string,
  tab: RecallForm["tab"],
): string {
  return tab === "external"
    ? externalUrl
    : `${resolverBaseUrl.replace(/\/$/, "")}${noticePath}`;
}
